/**
 * Brief field catalog — ported verbatim from the legacy Streamlit
 * (streamlit-internal/app.py) so the React recap panel is iso: same labels,
 * same grouping, same "filled" logic.
 *
 * NOTE: a few keys in OPERATION_FIELDS deliberately match the Streamlit source
 * exactly (e.g. `un_mois_un_geste_enseigne`, `grand_jeu_montant_dotation`) even
 * though they diverge slightly from FIELD_LABELS / the OperationType enum. This
 * preserves the existing behavior; fixing them is a separate follow-up.
 */
import type { BriefData } from "../../api/chat";

/** String values treated as "empty" for a field. */
export const EMPTY_TOKENS = new Set(["", "n/a", "na", "none", "null"]);

export const FIELD_LABELS: Record<string, string> = {
  centre_concerne: "Centre",
  enseigne_concernee: "Enseigne",
  sujet: "Sujet",
  type_operation: "Type d'opération",
  description_sujet: "Description",
  objectif: "Objectif",
  cible: "Cible",
  partenaires: "Partenaires",
  formats_livrables: "Sélection des livrables",
  precisions_livrables: "Précisions livrables",
  visuel_reference_url: "Inspirations / visuels",
  precision_composition_visuelle: "Précisions composition visuelle",
  visuel_composition_visuelle: "Visuels composition visuelle",
  titre_composition_visuelle: "Titre de la composition visuelle",
  sous_titre_composition_visuelle: "Sous-Titre de la composition visuelle",
  texte_redactionnel: "Texte rédactionnel",
  contraintes: "Contraintes",
  liens_redirection: "Liens de redirection",
  informations_suplementaires: "Informations supplémentaires",
  email_utilisateur: "Adresse mail",

  concours_mecanique_participation: "Mécanique de participation",
  concours_date_debut: "Date de début",
  concours_date_fin: "Date de fin",
  concours_horaires: "Horaires",
  concours_enseignes_participantes: "Enseignes participantes",
  concours_tirage_au_sort: "Tirage au sort",
  concours_lots: "Lots",
  concours_montant_dotation_eur: "Dotation totale (€)",
  concours_contact_gagnant: "Contact gagnant",
  concours_date_remise_lots: "Date remise des lots",

  event_lieu_emplacement: "Lieu / emplacement",
  event_date: "Date",
  event_dates_multiples: "Dates multiples",
  event_horaires: "Horaires",
  event_duree: "Durée",
  event_inscription: "Inscription nécessaire",
  event_lien_inscription: "Lien d’inscription",
  event_enseignes_participantes: "Enseignes participantes",
  event_partenaires: "Partenaires",
  event_animations: "Animations / activités",

  opco_type: "Type",
  opco_date_debut: "Date de début",
  opco_date_fin: "Date de fin",
  opco_enseignes_participantes: "Enseignes participantes",
  opco_offre: "Offre / mécanique",
  opco_conditions: "Conditions / exclusions",

  boutique_secteur: "Secteur d’activité",
  boutique_transfert: "Transfert",
  boutique_transfert_details: "Détails du transfert",
  boutique_date_ouverture: "Date d’ouverture",
  boutique_localisation: "Localisation",
  boutique_offre_ouverture: "Offre d’ouverture",

  act_for_good_thematique: "Thématique",
  act_for_good_date_debut: "Date de début",
  act_for_good_date_fin: "Date de fin",
  act_for_good_action_attendue: "Action attendue",
  act_for_good_incentive: "Incentive",

  vie_centre_ouvertures_exceptionnelles: "Ouvertures exceptionnelles",
  vie_centre_animations: "Animations",

  services_mis_en_avant: "Services mis en avant",
  services_cta: "Call-to-action",

  autre_precision: "Nature de l’opération",
  autre_periode: "Période / échéance",
  autre_informations_essentielles: "Informations essentielles",

  un_mois_un_geste_enseigne_nom: "Enseigne mise en avant",
  un_mois_un_geste_contenu: "Contenu / dispositif récurrent",
  un_mois_un_geste_frequence: "Fréquence",
  un_mois_un_geste_periode: "Période / mois",
  un_mois_un_geste_precision: "Informations complémentaires",

  grand_jeu_frequence: "Fréquence",
  grand_jeu_date_debut: "Date de début",
  grand_jeu_date_fin: "Date de fin",
  grand_jeu_type_dotation: "Type de dotation",
  grand_jeu_montant_carte_cadeau: "Montant carte cadeau (€)",
  grand_jeu_nb_gagnants: "Nombre de gagnants / cartes",
  grand_jeu_lien_qualifio: "Lien Qualifio",
  grand_jeu_theme: "Thème particulier",

  privileges_details: "Détail du privilèges",

  web_banniere_home_date_publication: "Date de publication",
  web_banniere_home_duree: "Durée d’affichage",
  web_article_angle: "Angle / contenu",
  web_article_date_publication: "Date de publication",
  crm_newsletter_date_envoi: "Date d’envoi",
  linktree_date_publication: "Date de publication",
  linktree_date_depublication: "Date de dépublication",
  linktree_liens_redirection: "Liens de redirection",

  social_post_compte_a_mentionner: "Comptes à mentionner",
  social_post_hashtags: "Hashtags",
  social_post_date_publication: "Date de publication",
  social_post_format: "Format",
  social_post_nb_slides: "Nombre de slides",

  social_story_compte_a_mentionner: "Comptes à mentionner",
  social_story_hashtags: "Hashtags",
  social_story_date_publication: "Date de publication",
  social_story_nb: "Nombre de stories",

  social_reel_compte_a_mentionner: "Comptes à mentionner",
  social_reel_hashtags: "Hashtags",
  social_reel_date_publication: "Date de publication",
  social_reel_duree: "Durée cible",
  social_reel_style: "Style souhaité",
  social_reel_idees: "Idées / inspirations",
};

export const OPERATION_LABELS: Record<string, string> = {
  jeu_concours: "Jeu concours",
  evenement: "Événement",
  operation_commerciale: "Opération commerciale",
  ouverture_transfert_boutique: "Ouverture / transfert de boutique",
  act_for_good: "ACT4GOOD",
  vie_du_centre: "Vie du centre",
  services: "Services",
  autre: "Autre",
  un_mois_un_geste: "Un mois un geste",
  grand_jeu: "Grand jeu",
  privileges: "Programme fidélité / privilèges",
};

export const DELIVERABLE_LABELS: Record<string, string> = {
  newsletter: "Newsletter (CRM)",
  banniere_home: "Bannière home (Web)",
  article_web: "Article web",
  post: "Post (Social)",
  story: "Story (Social)",
  reel: "Reel (Social)",
  linktree: "Linktree",
};

export const GENERAL_FIELDS: string[] = [
  "centre_concerne",
  "enseigne_concernee",
  "sujet",
  "type_operation",
  "description_sujet",
  "objectif",
  "cible",
  "partenaires",
  "formats_livrables",
  "precisions_livrables",
  "visuel_reference_url",
  "precision_composition_visuelle",
  "visuel_composition_visuelle",
  "titre_composition_visuelle",
  "sous_titre_composition_visuelle",
  "texte_redactionnel",
  "contraintes",
  "liens_redirection",
  "informations_suplementaires",
  "email_utilisateur",
];

export const OPERATION_FIELDS: Record<string, string[]> = {
  jeu_concours: [
    "concours_mecanique_participation",
    "concours_date_debut",
    "concours_date_fin",
    "concours_horaires",
    "concours_enseignes_participantes",
    "concours_tirage_au_sort",
    "concours_lots",
    "concours_montant_dotation_eur",
    "concours_contact_gagnant",
    "concours_date_remise_lots",
  ],
  evenement: [
    "event_lieu_emplacement",
    "event_date",
    "event_dates_multiples",
    "event_horaires",
    "event_duree",
    "event_inscription",
    "event_lien_inscription",
    "event_enseignes_participantes",
    "event_partenaires",
    "event_animations",
  ],
  operation_commerciale: [
    "opco_type",
    "opco_date_debut",
    "opco_date_fin",
    "opco_enseignes_participantes",
    "opco_offre",
    "opco_conditions",
  ],
  ouverture_transfert_boutique: [
    "boutique_secteur",
    "boutique_transfert",
    "boutique_transfert_details",
    "boutique_date_ouverture",
    "boutique_localisation",
    "boutique_offre_ouverture",
  ],
  act_for_good: [
    "act_for_good_thematique",
    "act_for_good_date_debut",
    "act_for_good_date_fin",
    "act_for_good_action_attendue",
    "act_for_good_incentive",
  ],
  vie_du_centre: ["vie_centre_ouvertures_exceptionnelles", "vie_centre_animations"],
  services: ["services_mis_en_avant", "services_cta"],
  autre: ["autre_precision", "autre_periode", "autre_informations_essentielles"],
  un_mois_un_geste_enseigne: [
    "un_mois_un_geste_enseigne_nom",
    "un_mois_un_geste_contenu",
    "un_mois_un_geste_frequence",
    "un_mois_un_geste_periode",
    "un_mois_un_geste_precision",
  ],
  grand_jeu: [
    "grand_jeu_frequence",
    "grand_jeu_date_debut",
    "grand_jeu_date_fin",
    "grand_jeu_type_dotation",
    "grand_jeu_montant_dotation",
    "grand_jeu_nombre_gagnants",
    "grand_jeu_lien_qualifio",
    "grand_jeu_theme",
  ],
  privileges: ["privileges_details"],
};

export const DELIVERABLE_FIELDS: Record<string, string[]> = {
  banniere_home: ["web_banniere_home_date_publication", "web_banniere_home_duree"],
  article_web: ["web_article_angle", "web_article_date_publication"],
  newsletter: ["crm_newsletter_date_envoi"],
  linktree: [
    "linktree_date_publication",
    "linktree_date_depublication",
    "linktree_liens_redirection",
  ],
  post: [
    "social_post_compte_a_mentionner",
    "social_post_hashtags",
    "social_post_date_publication",
    "social_post_format",
    "social_post_nb_slides",
  ],
  story: [
    "social_story_compte_a_mentionner",
    "social_story_hashtags",
    "social_story_date_publication",
    "social_story_nb",
  ],
  reel: [
    "social_reel_compte_a_mentionner",
    "social_reel_hashtags",
    "social_reel_date_publication",
    "social_reel_duree",
    "social_reel_style",
    "social_reel_idees",
  ],
};

// --- Value helpers (ported from app.py:1508-1591) ---------------------------

export function isFilledUi(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s !== "" && !EMPTY_TOKENS.has(s);
  }
  if (Array.isArray(v)) return v.some((x) => isFilledUi(x));
  return true;
}

export function formatValueUi(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") {
    const s = v.trim();
    if (s.toLowerCase() === "aucune" || s.toLowerCase() === "aucun") return "Aucun";
    return s;
  }
  if (Array.isArray(v)) {
    return v
      .map((x) => String(x).trim())
      .filter((x) => x !== "")
      .join(", ");
  }
  return String(v);
}

export function normalizeToList(v: unknown): string[] {
  if (v === null || v === undefined) return [];
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter((x) => x !== "");
  if (typeof v === "string")
    return v
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x !== "");
  return [String(v).trim()];
}

export const humanizeOperation = (code: unknown): string =>
  OPERATION_LABELS[String(code)] ?? String(code);

export const humanizeDeliverable = (code: unknown): string =>
  DELIVERABLE_LABELS[String(code)] ?? String(code);

export function formatFieldValue(key: string, value: unknown): string {
  if (!isFilledUi(value)) return "En attente...";
  if (key === "type_operation") return humanizeOperation(value);
  if (key === "formats_livrables")
    return normalizeToList(value).map(humanizeDeliverable).join(", ");
  return formatValueUi(value);
}

export function countFilledFields(data: BriefData, fields: string[]): number {
  return fields.reduce((n, key) => (isFilledUi(data[key]) ? n + 1 : n), 0);
}

export function fieldLabel(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
  );
}

export interface RecapField {
  key: string;
  label: string;
  category: string;
  value: string;
  ok: boolean;
}

/**
 * Build the unified recap field list (Principal + selected operation block +
 * selected deliverable blocks), mirroring app.py:2702-2723.
 */
export function buildRecapFields(data: BriefData): RecapField[] {
  const rows: Array<{ key: string; category: string }> = [];
  for (const key of GENERAL_FIELDS) rows.push({ key, category: "Principal" });

  const op = data["type_operation"];
  const opFields = op != null ? OPERATION_FIELDS[String(op)] : undefined;
  if (opFields) {
    const category = humanizeOperation(op);
    for (const key of opFields) rows.push({ key, category });
  }

  for (const deliv of normalizeToList(data["formats_livrables"])) {
    const delivFields = DELIVERABLE_FIELDS[deliv];
    if (delivFields) {
      const category = humanizeDeliverable(deliv);
      for (const key of delivFields) rows.push({ key, category });
    }
  }

  return rows.map(({ key, category }) => ({
    key,
    label: fieldLabel(key),
    category,
    value: formatFieldValue(key, data[key]),
    ok: isFilledUi(data[key]),
  }));
}
